import React from 'react';
import { ComparisonState, Theme, ViewMode, ObjectFit, Orientation, ExportMode } from '../types';
import { ImageUploader } from './ImageUploader';
import { SegmentedControl, RangeControl } from './Controls';
import { FONTS_LIST } from '../constants';
import { translations } from '../translations';

interface SidebarProps {
  state: ComparisonState;
  updateState: <K extends keyof ComparisonState>(key: K, value: ComparisonState[K]) => void;
  applyTheme: (theme: Theme) => void;
  handleExport: () => void;
  isExporting: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ 
  state, 
  updateState, 
  handleExport, 
  isExporting
}) => {
  const t = translations[state.language];

  const getExportBtnLabel = () => {
    if (isExporting) return t.exporting;
    switch(state.exportMode) {
      case 'dynamic': return t.copy_code_btn;
      case 'link': return t.copy_link_btn;
      default: return t.download_btn;
    }
  };

  return (
    <aside className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 space-y-8 sticky top-8 max-h-[90vh] overflow-y-auto custom-scrollbar">
      <div className="space-y-6">
        <h3 className="text-xs font-black uppercase tracking-[0.3em] text-indigo-400 mb-4">{t.media_config}</h3>
        <div className="grid grid-cols-2 gap-4">
          <ImageUploader 
            id="before" 
            label={t.image_before}
            onUpload={(url) => updateState('beforeImage', url)} 
            currentImage={state.beforeImage} 
            language={state.language}
          />
          <ImageUploader 
            id="after" 
            label={t.image_after}
            onUpload={(url) => updateState('afterImage', url)} 
            currentImage={state.afterImage} 
            language={state.language}
          />
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-xs font-black uppercase tracking-[0.3em] text-indigo-400 mb-4">{t.layout_format}</h3>
        <SegmentedControl 
          label={t.view_mode}
          options={[{id: 'slider', label: t.slider}, {id: 'side-by-side', label: t.side}]} 
          current={state.viewMode} 
          onChange={(val) => updateState('viewMode', val as ViewMode)} 
        />
        <SegmentedControl 
          label={t.orientation}
          options={[{id: 'horizontal', label: t.horiz}, {id: 'vertical', label: t.vert}]} 
          current={state.orientation} 
          onChange={(val) => updateState('orientation', val as Orientation)} 
        />
        <SegmentedControl 
          label={t.image_fit}
          options={[
            {id: 'cover', label: t.cover}, 
            {id: 'contain', label: t.contain, disabled: state.viewMode === 'slider'}
          ]} 
          current={state.viewMode === 'slider' ? 'cover' : state.objectFit} 
          onChange={(val) => updateState('objectFit', val as ObjectFit)} 
        />
        <div className="pt-2 space-y-4">
          <RangeControl label={t.corner_radius} value={state.borderRadius} min={0} max={100} onChange={v => updateState('borderRadius', v)} colorClass="accent-indigo-500" />
          <RangeControl label={t.padding} value={state.padding} min={0} max={100} onChange={v => updateState('padding', v)} colorClass="accent-indigo-500" />
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-xs font-black uppercase tracking-[0.3em] text-indigo-400 mb-4">{t.separator_design}</h3>
        <div className="flex gap-4">
          <div className="flex-1 space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">{t.color}</label>
            <input 
              type="color" 
              value={state.separatorColor} 
              onChange={e => updateState('separatorColor', e.target.value)}
              className="w-full h-10 bg-slate-950 border border-slate-800 rounded-lg cursor-pointer"
            />
          </div>
          <div className="flex-1">
            <RangeControl label={t.thickness} value={state.separatorWidth} min={0} max={20} onChange={v => updateState('separatorWidth', v)} colorClass="accent-indigo-500" />
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-xs font-black uppercase tracking-[0.3em] text-indigo-400 mb-4">{t.texts_captions}</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">{t.main_title}</label>
            <input 
              type="text" 
              value={state.title} 
              onChange={e => updateState('title', e.target.value)}
              placeholder={t.default_title}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-sm focus:border-indigo-500 outline-none transition-all"
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">{t.before_label_caption}</label>
              <input type="text" placeholder={t.default_before_label} value={state.beforeLabel} onChange={e => updateState('beforeLabel', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-sm outline-none mb-2" />
              <textarea placeholder={t.default_before_desc} value={state.beforeDesc} onChange={e => updateState('beforeDesc', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-xs outline-none h-20" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">{t.after_label_caption}</label>
              <input type="text" placeholder={t.default_after_label} value={state.afterLabel} onChange={e => updateState('afterLabel', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-sm outline-none mb-2" />
              <textarea placeholder={t.default_after_desc} value={state.afterDesc} onChange={e => updateState('afterDesc', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-xs outline-none h-20" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">{t.label_color}</label>
              <input 
                type="color" 
                value={state.labelColor} 
                onChange={e => updateState('labelColor', e.target.value)}
                className="w-full h-10 bg-slate-950 border border-slate-800 rounded-lg cursor-pointer"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">{t.desc_color}</label>
              <input 
                type="color" 
                value={state.descColor} 
                onChange={e => updateState('descColor', e.target.value)}
                className="w-full h-10 bg-slate-950 border border-slate-800 rounded-lg cursor-pointer"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">{t.font}</label>
            <select 
              value={state.fontFamily} onChange={e => updateState('fontFamily', e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs"
            >
              {FONTS_LIST.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <RangeControl label={t.font_size} value={state.fontSize} min={8} max={48} onChange={v => updateState('fontSize', v)} colorClass="accent-indigo-500" />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t border-slate-800">
        <h3 className="text-xs font-black uppercase tracking-[0.3em] text-indigo-400 mb-4">{t.export_mode}</h3>
        <SegmentedControl 
          label=""
          options={[
            {id: 'image', label: t.export_mode_image}, 
            {id: 'dynamic', label: t.export_mode_dynamic},
            {id: 'link', label: t.export_mode_link}
          ]} 
          current={state.exportMode} 
          onChange={(val) => updateState('exportMode', val as ExportMode)} 
        />
        
        <button 
          disabled={isExporting || (!state.beforeImage && !state.afterImage)}
          onClick={handleExport}
          className="w-full py-6 rounded-3xl bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 text-white font-black text-xl hover:shadow-[0_0_30px_rgba(168,85,247,0.3)] transition-all active:scale-95 disabled:opacity-50"
        >
          {getExportBtnLabel()}
        </button>
      </div>
    </aside>
  );
};